import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    selectedVehicleIds: [],
    selectedUnitIds: [],
    selectedCommandId: null,
    selectedSendingMethod: null,
    selectedSendingMethodVars: [],
    activeCommand: null,
    busySending: false
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties()
  }
}

function formDefaults () {
  return {}
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    const moduleData = context.getters['get']
    const commandId = moduleData.selectedCommandId
    const sendingMethod = moduleData.selectedSendingMethod
    const data = {
      command_id: commandId,
      sending_method: sendingMethod,
      variables: moduleData.selectedSendingMethodVars,
      vehicle_ids: moduleData.selectedVehicleIds,
      unit_ids: moduleData.selectedUnitIds
    }

    return ApiService
      .post(`unit/commands/send/${commandId}`, data)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`unit/commands/send/find`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryLogs (context, params) {
    return ApiService
      .get(`unit/commands/send/logs`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(defaults(), data)
  },

  setFormData (state, data) {
    state.form = {
    }
  },

  toggleSelectedVehicleId (state, vehicleId) {
    if (state.data.selectedVehicleIds.includes(vehicleId)) {
      state.data.selectedVehicleIds.splice(state.data.selectedVehicleIds.indexOf(vehicleId), 1)
    } else {
      state.data.selectedVehicleIds.push(vehicleId)
    }
  },

  addSelectedVehicleIds (state, vehicleIds) {
    state.data.selectedVehicleIds.push(...vehicleIds)
  },

  clearSelectedVehicleIds (state) {
    state.data.selectedVehicleIds = []
  },

  toggleSelectedUnitId (state, unitId) {
    if (state.data.selectedUnitIds.includes(unitId)) {
      state.data.selectedUnitIds.splice(state.data.selectedUnitIds.indexOf(unitId), 1)
    } else {
      state.data.selectedUnitIds.push(unitId)
    }
  },

  addSelectedUnitIds (state, unitIds) {
    state.data.selectedUnitIds.push(...unitIds)
  },

  clearSelectedUnitIds (state) {
    state.data.selectedUnitIds = []
  },

  updateSelectedSendingMethodVars (state, newVars) {
    newVars.forEach((newVar) => {
      const varIndex = state.data.selectedSendingMethodVars.findIndex((variable) => variable.name === newVar.name)
      state.data.selectedSendingMethodVars[varIndex].value = newVar.value
    })
  },

  setIsBusySending (state, value) {
    state.data.busySending = value
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = Object.assign(defaults(), data)
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/units/commands/commandSendingModule.js